import { FSM } from 'afsm';
import {
  fromEvent, ISink,
  Observable,
  pipe,
  scan, share, Subject,
  subject,
  subscribe, take,
  takeUntil
} from 'fastrx';
import { VideoCodecInfo, VideoDecoderEvent } from 'jv4-decoder/src/types';
import { RemoteVideoTrack, VideoDecoderDowngradeState, VideoPlayerMode, DECODE_FAILED_ERROR_CODE, RemoteTrack } from '..';
import { isMediaStreamTrackProcessorSupported } from '../common/rtc-detection';
import kvStatManager, { KV_REPORT_KEY_VIDEO_DECODE } from './kv-stat-manager';
import { VideoGlContext, Video2dContext } from './video-context';
import { VideoDestinationNode } from './video-node/destination';
import { YUVSourceNode } from './video-node/yuv';
import { performanceNow } from '../utils';
// export const webCodecsDowngradeTry = [(conf: VideoDecoderConfig | VideoEncoderConfig) => {
//   if ('latencyMode' in conf) delete conf.latencyMode;
//   if ('optimizeForLatency' in conf) delete conf.optimizeForLatency;
// }, (conf: VideoDecoderConfig) => {
//   delete conf.hardwareAcceleration;
// }];
const getKvKey = (decoderType: string, renderer: string): KV_REPORT_KEY_VIDEO_DECODE => {
  switch (decoderType) {
    case 'webCodecs':
      return renderer === 'videoFrame' ? KV_REPORT_KEY_VIDEO_DECODE.DOWNGRADE_WEBCODECS_VIDEO : KV_REPORT_KEY_VIDEO_DECODE.DOWNGRADE_WEBCODECS_2D;
    case 'wasm':
      return renderer === 'webgl' ? KV_REPORT_KEY_VIDEO_DECODE.DOWNGRADE_WASM_WEGBL : renderer === 'videoFrame' ? KV_REPORT_KEY_VIDEO_DECODE.DOWNGRADE_WASM_VIDEO : KV_REPORT_KEY_VIDEO_DECODE.DOWNGRADE_WASM_2D;
  }
  throw new Error('decoder type not supported');
};
function isH26x(data: Uint8Array) {
  return data[0] === 0 && data[1] === 0 && data[2] === 0 && data[3] === 1;
}
export interface DecodeContextOption {
  type: 'webCodecs' | 'wasm' | 'auto';
  renderer: 'webgl' | '2d' | 'videoFrame';
  fallback: 'auto' | 'wasm' | 'wasm_2d' | 'wasm_video';
  workerMode?: boolean;
  canvas?: HTMLCanvasElement;
  wasmPath?: string;
  videoElement?: HTMLVideoElement;
  config: VideoDecoderConfig;
  track: RemoteVideoTrack;
  createDecoder: (type: 'webCodecs' | 'wasm', wasmOption?: {
    workerMode?: boolean;
    canvas?: HTMLCanvasElement;
    wasmPath?: string;
    yuvMode?: boolean;
  }) => IVideoDecoder;
}

export interface IVideoDecoder extends FSM {
  initialize(element?: HTMLVideoElement): Promise<void>;
  configure(config: VideoDecoderConfig): void;
  decode(data: EncodedVideoChunkInit): void;
  close(): void;
}
let id = 0;
export class DecodeContext {
  id = id++;
  trackDoneOB: Observable<string>;
  startOB: Subject<number>;
  stopOB: Subject<any>;
  decoder?: IVideoDecoder;
  videoContext: VideoGlContext | Video2dContext;
  gop = 0;
  gop_helper = 0;
  waitFirstKeyFrame = true;
  startTimestamp = 0;
  startTime = 0;
  startPerformanceTime = 0;
  inputFrameCount = 0;
  decodedFrameCount = 0;
  decodeFrameCount = 0;
  downgradeLevel = 0;
  lastDowngradeTime = 0;
  lastFrameDiff = 0; // 添加记录上一次帧差的变量
  lastDecodeFrameTimestamp = 0;
  config: VideoDecoderConfig;
  gop_before_configure: RTCEncodedVideoFrame[] = [];
  videoElement?: HTMLVideoElement;
  type: 'wasm' | 'webCodecs' = 'wasm';
  goodType?: 'wasm' | 'webCodecs';
  renderer: '2d' | 'webgl' | 'videoFrame' = '2d';
  wasmOption: {
    workerMode?: boolean;
    canvas?: HTMLCanvasElement;
    wasmPath?: string;
    yuvMode?: boolean;
  };
  createDecoder: DecodeContextOption['createDecoder'];
  private _decodeSink?: ISink<VideoFrame>;
  isReported = false;
  track: RemoteVideoTrack;
  stateChangeOB: Subject<VideoDecoderDowngradeState>;
  failedReason: string;
  constructor(option: DecodeContextOption) {
    const { track, createDecoder } = option;
    this.stateChangeOB = subject();
    this.track = track;
    this.createDecoder = createDecoder;
    this.wasmOption = {
      yuvMode: option.renderer === 'webgl',
      wasmPath: option.wasmPath,
      workerMode: option.workerMode,
      canvas: option.canvas
    };
    this.config = option.config;
    this.videoElement = option.videoElement;

    this.renderer = option.renderer;
    this.trackDoneOB = fromEvent(track.availableState, FSM.OFF);

    this.stopOB = subject();
    if (option.type === 'auto') {
      switch (option.fallback) {
        case 'wasm':
          this.type = 'wasm';
          this.renderer = 'webgl';
          break;
        case 'wasm_2d':
          this.type = 'wasm';
          this.renderer = '2d';
          break;
        case 'wasm_video':
          this.type = 'wasm';
          this.renderer = 'videoFrame';
          break;
        default:
          this.type = 'webCodecs';
      }
      this.wasmOption.yuvMode = this.renderer === 'webgl';
    } else this.type = option.type;
    this.changeRenderer(this.renderer);
    // 状态变化逻辑
    pipe(this.stateChangeOB, scan<VideoDecoderDowngradeState, VideoDecoderDowngradeState, VideoDecoderDowngradeState>((prevState, state) => {
      if (prevState !== state) track.onDecodeDowngradeStateChanged({
        type: this.type,
        renderer: this.renderer,
        reason: this.failedReason,
        prevState,
        state
      });
      return state;
    }, VideoDecoderDowngradeState.INITIALIZED), takeUntil(this.stopOB), subscribe());
    this.start();
  }
  start(retryCount = 0) {
    this.waitFirstKeyFrame = true;
    this.stateChangeOB.next(VideoDecoderDowngradeState.STARTING);
    // 主流程： 从触发解码到完成解码，中途出错，尝试降级和重试，重试次数超过一定次数会最终失败。
    const decoderOB = pipe(this.pipe(this.track), takeUntil(this.stopOB), share());
    pipe(decoderOB, subscribe(() => {
      this.track.stat.framesDecoded++;
    }, err => {
      this.track.log.error(`${this.id} play failed: ${err} retryCount: ${retryCount}`);
      kvStatManager.addFailedEvent({ key: getKvKey(this.type, this.renderer), error: err });
      if (retryCount > 4) {
        // 最终失败
        this.failedReason = err;
        this.stateChangeOB.next(VideoDecoderDowngradeState.FAILED);
        kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_VIDEO_DECODE.DOWNGRADE_RESULT });
      } else { // 重试/降级
        if (this.goodType) {
          this.start(retryCount);
          return;
        }
        switch (this.type) {
          case 'webCodecs':
            this.type = 'wasm';
            this.changeRenderer('webgl');
            break;
          case 'wasm':
            switch (this.renderer) {
              case 'webgl':
                this.changeRenderer('videoFrame');
                break;
            }
        }
        this.start(retryCount + 1);
      }
    }, () => {
      this.track.log.warn(`${this.id} decoderOB completed`);
      kvStatManager.addSuccessEvent({ key: getKvKey(this.type, this.renderer) });
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_VIDEO_DECODE.DOWNGRADE_RESULT });
    }));
    // 成功解码第一帧
    pipe(decoderOB, take(1), subscribe(() => {
      this.track.player.handlePlaying('canvas');
      this.goodType = this.type;
      this.stateChangeOB.next(VideoDecoderDowngradeState.STARTED);
    }));
  }
  mock(err: DECODE_FAILED_ERROR_CODE) {
    if (this._decodeSink) this._decodeSink.error(err);
    else this.start();
  }
  close(reason: string) {
    this.stopOB.next(reason);
  }
  changeRenderer(renderer: DecodeContextOption['renderer']) {
    this.renderer = renderer;
    if (this.renderer === 'videoFrame' && !isMediaStreamTrackProcessorSupported()) this.renderer = '2d'; // 不支持 MediaStreamTrackGenerator 只能用 2d 渲染
    this.wasmOption.yuvMode = this.renderer === 'webgl';
  }
  decode(data: RTCEncodedVideoFrame, isCache = false) {
    if (this.failedReason) return;
    this.inputFrameCount++;
    const uint8Array = new Uint8Array(data.data);
    if (!isH26x(uint8Array) || uint8Array.length < 5) {
      this.stateChangeOB.next(VideoDecoderDowngradeState.FAILED);
      this.close(`not h26x frame ${uint8Array.subarray(0, 5)}`);
      return data;
    }
    const type = uint8Array[4] & 0x1f;
    let isKeyFrame = false;
    switch (type) {
      case 5:
      case 7:
        isKeyFrame = true;
        break;
    }
    if (this.decoder?.state !== 'configured') {
      this.track.log.debug(`not configured ${this.inputFrameCount}`);
      if (isKeyFrame) {
        this.gop_before_configure = [];
      }
      this.gop_before_configure.push({ data: data.data, timestamp: data.timestamp, type: data.type });
      return data;
    }
    if (this.gop_before_configure.length > 0 && !isCache) {
      this.gop_before_configure.forEach(frame => this.decode(frame, true));
      this.gop_before_configure = [];
    }
    const { timestamp } = data;
    if (isKeyFrame) {
      this.gop = this.gop_helper;
      this.gop_helper = 0;
    } else {
      this.gop_helper++;
    }
    if (this.decoder) {
      if (this.waitFirstKeyFrame) {
        if (isKeyFrame) {
          this.waitFirstKeyFrame = false;
          this.startTimestamp = timestamp;
          this.startTime = Date.now();
          this.startPerformanceTime = performanceNow();
        } else {
          this.track.log.debug(`wait first key frame ${this.inputFrameCount} ${uint8Array.subarray(0, 5).join(' ')}`);
          return;
        }
      }

      switch (this.downgradeLevel) {
        case 0: // good
          break;
        case 1: // half render
          break;
        case 2: // half gop
          if (this.gop_helper > (this.gop >> 1)) {
            return;
          }
          break;
        case 3: // only i frame
          if (this.gop_helper > 0) {
            return;
          }
          break;
        default: // skip decode
          return;
      }
      if (this.decodeFrameCount < 10 || (this.decodeFrameCount % 500 === 0)) this.track.log.debug(`decode ${this.decodeFrameCount} gop: ${this.gop} ${timestamp} ${data.getMetadata?.().rtpTimestamp}`);
      this.decodeFrameCount++;
      this.lastDecodeFrameTimestamp = timestamp;
      this.decoder.decode({ data: data.data, type: data.type, timestamp: this.lastDecodeFrameTimestamp } as EncodedVideoChunkInit);
      return;
    }
    return data;
  }
  checkDowngradeByFrameDiff() {
    const lastDowngradeLevel = this.downgradeLevel;
    // 修改降级策略计算方法
    const frameDiff = this.decodeFrameCount - this.decodedFrameCount;
    // 比较当前帧差与上一次帧差
    if (frameDiff > this.lastFrameDiff) {
      // 如果帧差在增加，说明积压加重，增加降级级别
      this.downgradeLevel++;
      if (this.downgradeLevel > 4) {
        this.downgradeLevel = 4;
      }
    } else if (frameDiff <= this.lastFrameDiff && this.downgradeLevel > 0) {
      // 如果帧差在减少，说明积压在缓解，可以考虑降低降级级别
      this.downgradeLevel--;
    }
    if (this.downgradeLevel !== lastDowngradeLevel) this.track.log.debug(`downgrade level ${lastDowngradeLevel} to ${this.downgradeLevel} ${this.decodeFrameCount} frameDiff: ${frameDiff}, lastFrameDiff: ${this.lastFrameDiff}`);
    // 更新上一次帧差记录
    this.lastFrameDiff = frameDiff;
    this.lastDowngradeTime = Date.now();
  }
  checkDowngradeByTimestampDiff(timestamp: number) {
    const lastDowngradeLevel = this.downgradeLevel;
    if (this.lastDecodeFrameTimestamp - timestamp > 90000) {
      this.downgradeLevel++;
      if (this.downgradeLevel > 4) {
        this.downgradeLevel = 4;
      }
    } else if (this.downgradeLevel > 0) {
      this.downgradeLevel--;
    }
    if (this.downgradeLevel !== lastDowngradeLevel) this.track.log.debug(`downgrade level ${lastDowngradeLevel} to ${this.downgradeLevel}`);
  }
  pipe(track: RemoteVideoTrack): Observable<VideoFrame> {
    return (async sink => {
      this._decodeSink = sink;
      let decoder: IVideoDecoder | undefined;
      const originalTrack = track.mediaTrack;
      sink.defer(() => { // 取消订阅
        if (originalTrack) {
          track.player.setCanvas();
          track.setInputMediaStreamTrack(originalTrack);
        }
        decoder?.close();
        this.videoContext?.destroy();
        delete this._decodeSink;
      });
      const { renderer, type } = this;
      track.log.info(`decoder type: ${this.type} renderer: ${this.renderer}`);
      try {
        switch (type) {
          case 'wasm':
            decoder = this.createDecoder(type, this.wasmOption);
            break;
          case 'webCodecs':
            // for (let i = 0; i <= webCodecsDowngradeTry.length; i++) {
            //   const { supported } = await VideoDecoder.isConfigSupported(this.config);
            //   if (!supported) {
            //     if (i === webCodecsDowngradeTry.length) {
            //       sink.error(DECODE_FAILED_ERROR_CODE.WEBCODEC_CONFIG_NOT_SUPPORT);
            //       return;
            //     }
            //     webCodecsDowngradeTry[i](this.config);
            //   } else {
            //     break;
            //   }
            // }
            decoder = this.createDecoder(type);
            break;
          default:
            throw new Error('not supported yet');
        }
        let frameCounter = 0;
        decoder!.on(VideoDecoderEvent.VideoFrame, (frame: VideoFrame) => {
          this.decodedFrameCount++;
          frameCounter++;
          if (frameCounter <= 10 || frameCounter % 500 === 0) {
            track.log.debug(`frame ${frameCounter} ${this.decodedFrameCount}/${this.decodeFrameCount} decoded ${frame.timestamp}`);
          }
          if (Date.now() - this.lastDowngradeTime > 5000) {
            if (this.type === 'webCodecs') {
              this.checkDowngradeByFrameDiff();
            } else if (this.type === 'wasm') {
              this.checkDowngradeByTimestampDiff(frame.timestamp);
            }
          }
          sink.next(frame);
        });
        decoder!.on(VideoDecoderEvent.Error, (err: VideoDecoderEvent) => {
          track.log.error(err);
          sink.error(type === 'webCodecs' ? DECODE_FAILED_ERROR_CODE.WEBCODEC_DECODER_ERROR : DECODE_FAILED_ERROR_CODE.WASM_DECODER_ERROR);
        });
        await decoder!.initialize(this.videoElement);
        if (!this._decodeSink) return; // 如果已经取消订阅，则直接返回
        decoder!.configure(this.config);
        if (type === 'wasm' && renderer === 'webgl') {
          this.videoContext = new VideoGlContext({ frameRate: 15, logger: track.log, name: track.userId });
          this.videoContext.create();
          this.videoContext.on(VideoGlContext.UNAVAILABLE, (reason: any) => {
            track.log.error(reason);
            sink.error(DECODE_FAILED_ERROR_CODE.WASM_WEBGL_UNAVALIABLE);
          });
          const source = new YUVSourceNode(this.videoContext);
          decoder.on(VideoDecoderEvent.VideoCodecInfo, (info: VideoCodecInfo) => source.resize(info.width, info.height));
          decoder.on(VideoDecoderEvent.VideoFrame, (frame: { y: Uint8Array, u: Uint8Array, v: Uint8Array; }) => {
            ({ y: source.Y, u: source.U, v: source.V } = frame);
            if (this.downgradeLevel === 1) {
              if (this.decodedFrameCount % 2 === 0) source.render(this.decodedFrameCount);
            } else {
              source.render(this.decodedFrameCount);
            }
          });
          track.source = source;
          track.player.setCanvas(this.videoContext._canvas, VideoPlayerMode.CANVAS_WITHOUT_ELEMENT);
        } else if (renderer === 'videoFrame') {
          track.player.setCanvas();
          const trackGenerator = new MediaStreamTrackGenerator({ kind: 'video' });
          const writer = trackGenerator.writable.getWriter();
          track.setInputMediaStreamTrack(trackGenerator);
          decoder.on(VideoDecoderEvent.VideoFrame, (frame: VideoFrame) => writer.write(frame));
        } else {
          this.videoContext = new Video2dContext({ frameRate: 15, logger: track.log, name: track.userId });
          this.videoContext.create({ alpha: false });
          const source = this.videoContext.createVideoImageSource();
          decoder.on(VideoDecoderEvent.VideoFrame, (frame: VideoFrame) => {
            try {
              source.image = frame;
              source.update();
            } catch (err) {
              delete this.goodType;
              track.log.error(err);
              sink.error(DECODE_FAILED_ERROR_CODE.RENDER_2D_ERROR);
            }
          });
          const dest = new VideoDestinationNode(this.videoContext, { name: 'remotePlayer', logger: track.log });
          source.connect(dest);
          track.source = source;
          track.player.setCanvas(this.videoContext._canvas, VideoPlayerMode.CANVAS_WITHOUT_ELEMENT);
        }
        this.decoder = decoder;
      } catch (err) {
        track.log.error(err);
        sink.error(type === 'webCodecs' ? DECODE_FAILED_ERROR_CODE.WEBCODEC_INIT : DECODE_FAILED_ERROR_CODE.WASM_INIT);
      }
    });
  }
}
